import {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ModernFootermenu from '../../../components/layout/Footer';
import ModernHeader from '../../../components/layout/Headers/ModernHeader';
import ActivityList from './ActivityList';
import NoContent from '../../component/NoContent/NoContent';
import { getUserRole, isUserLoggedIn } from '../../../shared/utils/function';
import { ROLE } from '../../../shared/constants/common';
import {
  MESSAGE_TABS_BY_ROLE,
  getFetchableActivityTabIds,
  getArchivedActivitySectionTabIds,
  getActivitySliceKey,
  getActivityGroupedForTab,
  ACTIVITY_TAB_ICONS,
  ACTIVITY_PAGE_LIMIT,
} from '../../../shared/constants/activity';
import {
  mapCreatorNotifications,
  getActivityTabUnreadCount,
  reconcileActivitySliceUnreadCount,
  activityPaginationHasMore,
} from '../../../shared/utils/activityUtil';
import {
  fetchCreatorActivityTab,
  markAllLoadedActivityNotificationsViewed,
} from '../../../shared/actions/activityAction';
import { CenteredLoader } from '../../../shared/utils/chatUtil';
import { MESSAGE_READ } from '../../../shared/constants/common';

const ACTIVITY_EMPTY_MESSAGE = 'Nothing here yet!';

const Activity = () => {
  const dispatch = useDispatch();
  const { byTab } = useSelector((state) => state.activity);
  const syncRevision = useSelector(
    (state) => state.notifications?.syncRevision ?? 0
  );
  const [activeTab, setActiveTab] = useState('all');
  const prevActiveTabRef = useRef(null);
  const prevNotificationSyncRef = useRef(syncRevision);
  const loadMoreSentinelRef = useRef(null);
  const loadMoreInFlightRef = useRef(false);
  const userRole = useMemo(() => getUserRole(), []);
  const tabs = useMemo(() => MESSAGE_TABS_BY_ROLE[userRole] || [], [userRole]);
  const fetchableTabIds = useMemo(
    () => getFetchableActivityTabIds(userRole),
    [userRole]
  );
  /** "All" aggregates every category except Archived (dedicated Archived tab). */
  const fetchableTabIdsForAllTab = useMemo(
    () => fetchableTabIds.filter((id) => id !== 'archived'),
    [fetchableTabIds]
  );
  const archivedSectionTabIds = useMemo(
    () => getArchivedActivitySectionTabIds(userRole),
    [userRole]
  );

  /** After lists settle (or tab switch), PATCH viewed for unread rows so badges stay accurate. */
  useEffect(() => {
    if (!isUserLoggedIn()) return undefined;

    const id = window.setTimeout(() => {
      dispatch(markAllLoadedActivityNotificationsViewed(userRole));
    }, 600);

    return () => window.clearTimeout(id);
  }, [byTab, activeTab, dispatch, userRole]);

  const dispatchFetchCreatorActivityTab = useCallback(
    (tabId, { page, grouped, append = false, archived = false }) => {
      if (!isUserLoggedIn()) return;
      dispatch(
        fetchCreatorActivityTab(tabId, {
          page,
          limit: ACTIVITY_PAGE_LIMIT,
          append,
          role: userRole,
          grouped,
          archived,
        })
      );
    },
    [dispatch, userRole]
  );

  useEffect(() => {
    if (!isUserLoggedIn()) return;

    const tabChanged =
      prevActiveTabRef.current !== null &&
      prevActiveTabRef.current !== activeTab;
    prevActiveTabRef.current = activeTab;

    const pageForVariant = (tabId, { grouped, archived = false } = {}) => {
      const resolvedGrouped = grouped ?? getActivityGroupedForTab(tabId);
      const sliceKey = getActivitySliceKey({ grouped: resolvedGrouped, archived });
      const variant = byTab?.[tabId]?.byGrouped?.[sliceKey];
      const page = variant?.pagination?.page ?? 1;

      // When switching tabs, always reset pagination back to the first page
      // (so we don't keep showing e.g. page 3 of the old tab/new tab state).
      if (tabChanged && page > 1) return 1;

      return page >= 1 ? page : 1;
    };

    const shouldFetchVariant = (tabId, { grouped, archived = false } = {}) => {
      const resolvedGrouped = grouped ?? getActivityGroupedForTab(tabId);
      const sliceKey = getActivitySliceKey({ grouped: resolvedGrouped, archived });
      const variant = byTab?.[tabId]?.byGrouped?.[sliceKey];
      if (!variant?.loading && !variant?.fetched) return true;
      const page = variant?.pagination?.page ?? 1;
      if (tabChanged && page > 1) return true; // we will fetch page=1 via pageForVariant()
      return false;
    };

    if (activeTab === 'all') {
      fetchableTabIdsForAllTab.forEach((tabId) => {
        const grouped = getActivityGroupedForTab(tabId);
        if (!shouldFetchVariant(tabId, { grouped })) return;
        dispatchFetchCreatorActivityTab(tabId, {
          page: pageForVariant(tabId, { grouped }),
          grouped,
        });
      });
      return;
    }

    if (activeTab === 'archived') {
      archivedSectionTabIds.forEach((tabId) => {
        const grouped = getActivityGroupedForTab(tabId);
        if (!shouldFetchVariant(tabId, { grouped, archived: true })) return;
        dispatchFetchCreatorActivityTab(tabId, {
          page: pageForVariant(tabId, { grouped, archived: true }),
          grouped,
          archived: true,
        });
      });
      return;
    }

    const grouped = getActivityGroupedForTab(activeTab);
    if (!shouldFetchVariant(activeTab, { grouped })) return;
    dispatchFetchCreatorActivityTab(activeTab, {
      page: pageForVariant(activeTab, { grouped }),
      grouped,
    });
  }, [
    dispatchFetchCreatorActivityTab,
    fetchableTabIds,
    fetchableTabIdsForAllTab,
    archivedSectionTabIds,
    activeTab,
    byTab,
  ]);

  // After notification count polls (or other count refreshes), reload visible activity tabs.
  // Skip the 0→1 transition so we do not duplicate the initial tab fetch.
  useEffect(() => {
    if (!isUserLoggedIn()) return;

    if (prevNotificationSyncRef.current === syncRevision) return;
    const previous = prevNotificationSyncRef.current;
    prevNotificationSyncRef.current = syncRevision;
    if (previous === 0 && syncRevision === 1) return;
    if (!fetchableTabIds.length) return;

    if (activeTab === 'all') {
      fetchableTabIdsForAllTab.forEach((tabId) => {
        const grouped = getActivityGroupedForTab(tabId);
        const sliceKey = getActivitySliceKey({ grouped });
        const variant = byTab?.[tabId]?.byGrouped?.[sliceKey];
        const page = variant?.pagination?.page ?? 1;
        dispatchFetchCreatorActivityTab(tabId, {
          page,
          grouped,
        });
      });
      return;
    }

    if (activeTab === 'archived') {
      archivedSectionTabIds.forEach((tabId) => {
        const grouped = getActivityGroupedForTab(tabId);
        const sliceKey = getActivitySliceKey({ archived: true });
        const variant = byTab?.[tabId]?.byGrouped?.[sliceKey];
        const page = variant?.pagination?.page ?? 1;
        dispatchFetchCreatorActivityTab(tabId, {
          page,
          grouped,
          archived: true,
        });
      });
      return;
    }

    dispatchFetchCreatorActivityTab(activeTab, {
      page: 1,
      grouped: getActivityGroupedForTab(activeTab),
    });
  }, [
    syncRevision,
    activeTab,
    fetchableTabIds,
    fetchableTabIdsForAllTab,
    archivedSectionTabIds,
    byTab,
    dispatchFetchCreatorActivityTab,
  ]);

  const loadTabPage = useCallback(
    (tabId, page, append = false, archived = false) => {
      dispatchFetchCreatorActivityTab(tabId, {
        page,
        grouped: getActivityGroupedForTab(tabId),
        append,
        archived,
      });
    },
    [dispatchFetchCreatorActivityTab]
  );

  const isArchivedTab = activeTab === 'archived';
  const isAggregatedTab = activeTab === 'all' || isArchivedTab;

  const sections = useMemo(() => {
    const tabIds = (() => {
      if (activeTab === 'all') return fetchableTabIdsForAllTab;
      if (isArchivedTab) return archivedSectionTabIds;
      return fetchableTabIds;
    })();
    return tabIds.map((tabId) => {
      const meta = tabs.find((t) => t.id === tabId);
      const sliceKey = isArchivedTab
        ? getActivitySliceKey({ archived: true })
        : getActivitySliceKey({ grouped: getActivityGroupedForTab(tabId) });
      const slice = byTab?.[tabId]?.byGrouped?.[sliceKey] || {};
      return {
        tabId,
        title: meta?.label || tabId,
        icon: ACTIVITY_TAB_ICONS[tabId] || 'bell',
        colorPink:
          tabId === 'priority_messages' &&
          (userRole === ROLE.CREATOR_SUB || userRole === ROLE.USER),
        rawItems: slice.items || [],
        pagination: slice.pagination,
        loading: !!slice.loading,
        error: slice.error,
        fetched: !!slice.fetched,
        unreadCount: reconcileActivitySliceUnreadCount(slice),
      };
    });
  }, [
    fetchableTabIds,
    fetchableTabIdsForAllTab,
    archivedSectionTabIds,
    tabs,
    byTab,
    userRole,
    activeTab,
    isArchivedTab,
  ]);

  const activeSection = useMemo(() => {
    if (isAggregatedTab) return null;
    return sections.find((s) => s.tabId === activeTab) ?? null;
  }, [sections, activeTab, isAggregatedTab]);

  const activeTabHasMore = useMemo(() => {
    if (!activeSection?.pagination) return false;
    const currentPage = activeSection.pagination.page ?? 1;
    const totalPages = Math.max(1, activeSection.pagination.total_pages || 1);
    return currentPage < totalPages;
  }, [activeSection]);

  const loadMoreActiveTab = useCallback(() => {
    if (!activeSection || isAggregatedTab) return;
    if (!activeTabHasMore || activeSection.loading || loadMoreInFlightRef.current) {
      return;
    }
    loadMoreInFlightRef.current = true;
    const currentPage = activeSection.pagination?.page ?? 1;
    loadTabPage(activeTab, currentPage + 1, true);
  }, [activeSection, activeTab, activeTabHasMore, loadTabPage, isAggregatedTab]);

  useEffect(() => {
    if (!activeSection?.loading) {
      loadMoreInFlightRef.current = false;
    }
  }, [activeSection?.loading]);

  const prevScrollTabRef = useRef(activeTab);
  useEffect(() => {
    if (prevScrollTabRef.current === activeTab) return;
    prevScrollTabRef.current = activeTab;
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [activeTab]);

  useEffect(() => {
    if (isAggregatedTab || !activeSection?.fetched || !activeTabHasMore) return;

    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMoreActiveTab();
        }
      },
      { root: null, rootMargin: '200px', threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => {
      observer.unobserve(sentinel);
      observer.disconnect();
    };
  }, [
    isAggregatedTab,
    activeSection?.fetched,
    activeSection?.loading,
    activeSection?.pagination?.page,
    activeTabHasMore,
    loadMoreActiveTab,
  ]);

  const showAllTabsInitialLoader = useMemo(() => {
    if (!sections.length) return false;
    const hasAnyItems = sections.some((s) => s.rawItems.length > 0);
    const anyLoadingEmpty = sections.some(
      (s) => s.loading && !s.rawItems.length
    );
    const anyNotFetchedYet = sections.some(
      (s) => !s.fetched && !s.rawItems.length
    );
    return !hasAnyItems && (anyLoadingEmpty || anyNotFetchedYet);
  }, [sections]);

  const renderCenteredLoader = () => (
    <div className="activity_list_loader_wrap d-flex align-items-center justify-content-center">
      <CenteredLoader size={40} text="Loading..." />
    </div>
  );

  const loadMoreArchivedSection = useCallback(
    (tabId) => {
      const sliceKey = getActivitySliceKey({ archived: true });
      const slice = byTab?.[tabId]?.byGrouped?.[sliceKey];
      if (!slice || slice.loading) return;
      if (!activityPaginationHasMore(slice.pagination)) return;
      const currentPage = slice.pagination?.page ?? 1;
      loadTabPage(tabId, currentPage + 1, true, true);
    },
    [byTab, loadTabPage]
  );

  const renderSectionBlock = (section) => {
    if (section.loading && !section.rawItems.length) {
      return null;
    }

    if (section.error && !section.rawItems.length && isUserLoggedIn()) {
      return (
        <div key={section.tabId} className="mb-4">
          <h3 className="h6">{section.title}</h3>
          <p className="text-muted small mb-0">{section.error}</p>
        </div>
      );
    }

    if (!section.rawItems.length) {
      return null;
    }

    const mapped = mapCreatorNotifications(section.rawItems);
    const sectionHasMore =
      isArchivedTab && activityPaginationHasMore(section.pagination);
    const isLoadingMore = section.loading && section.rawItems.length > 0;

    const list = (
      <ActivityList
        title={section.title}
        icon={section.icon}
        data={mapped}
        totalCount={section.unreadCount}
        colorPink={section.colorPink}
        tabId={section.tabId}
        grouped={getActivityGroupedForTab(section.tabId)}
        isArchived={isArchivedTab}
      />
    );

    if (!sectionHasMore || !section.fetched) {
      return <div key={section.tabId}>{list}</div>;
    }

    return (
      <div key={section.tabId} className="activity_archived_section">
        {list}
        <div className="d-flex justify-center w-full">
          <button
            type="button"
            className="btn btn_primary rounded_8"
            disabled={isLoadingMore}
            onClick={() => loadMoreArchivedSection(section.tabId)}
            aria-busy={isLoadingMore}
          >
            {isLoadingMore ? 'Loading...' : MESSAGE_READ.SHOW_MORE_TEXT}
          </button>
        </div>
      </div>
    );
  };

  const renderAggregatedSections = () => {
    if (showAllTabsInitialLoader) {
      return renderCenteredLoader();
    }
    const blocks = sections.map(renderSectionBlock);
    const showEmpty =
      sections.every(
        (s) => !s.loading && !s.rawItems.length && !s.error
      );
    return (
      <>
        {blocks}
        {showEmpty ? <NoContent message={ACTIVITY_EMPTY_MESSAGE} /> : null}
      </>
    );
  };

  const renderSection = () => {
    if (!fetchableTabIds.length) {
      return <NoContent message={ACTIVITY_EMPTY_MESSAGE} />;
    }

    if (isAggregatedTab) {
      return renderAggregatedSections();
    }

    const section = sections.find((s) => s.tabId === activeTab);
    if (!section) {
      return null;
    }

    const isLoadingMore = section.loading && section.rawItems.length > 0;

    if (section.loading && !section.rawItems.length) {
      return renderCenteredLoader();
    }

    if (section.error && !section.rawItems.length && isUserLoggedIn()) {
      return (
        <div className="activity_section_error">
          <p className="text-muted mb-0">{section.error}</p>
        </div>
      );
    }

    const mapped = mapCreatorNotifications(section.rawItems);

    if (!mapped.length) {
      return <NoContent message={ACTIVITY_EMPTY_MESSAGE} />;
    }

    return (
      <>
        <ActivityList
          key={section.tabId}
          title={section.title}
          icon={section.icon}
          data={mapped}
          totalCount={section.unreadCount}
          colorPink={section.colorPink}
          tabId={activeTab}
          grouped={getActivityGroupedForTab(section.tabId)}
        />
        {section.fetched && activeTabHasMore ? (
          <div
            ref={loadMoreSentinelRef}
            className="activity_load_more_sentinel"
            aria-hidden
          />
        ) : null}
        {isLoadingMore ? (
          <div
            className="activity_list_loader_wrap activity_list_loader_wrap_more"
            role="status"
            aria-live="polite"
            aria-label="Loading more activity"
          >
            <CenteredLoader size={32} text="" />
          </div>
        ) : null}
      </>
    );
  };

  return (
    <div className="activity-screen">
      <ModernHeader
        customTitle="Activity"
        showButtonBorder={false}
        hideSearchIcon
        showAvatar={false}
        showBackButton={false}
      />

      <div className="activity_wrapper">
        <div className="msg_filter_bar">
          {tabs.map((tab) => {
            const unread = getActivityTabUnreadCount(tab.id, byTab);
            return (
              <span
                key={tab.id}
                className={activeTab === tab.id ? 'active' : ''}
                onClick={() => setActiveTab(tab.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setActiveTab(tab.id);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                {tab.label}
                {unread > 0 ? (
                  <span className="activity_tab_unread"> {unread}</span>
                ) : null}
              </span>
            );
          })}
        </div>

        <div className="activity_list">{renderSection()}</div>
      </div>

      <ModernFootermenu />
    </div>
  );
};

export default Activity;
